import { useMemo } from "react";
import { useRouter } from "next/router";
import { fixedCategoryItems } from "~/components/blocks/vod/top/common/category-carousel/const/fixed-categories";
import type { VodHrefMap } from "~/const/hrefs";
import { OTHER_LINK } from "~/const/links";
import { PATHS } from "~/const/paths";
import { useQueryString } from "~/hooks/use-query-string";
import { useRouterForVodList } from "~/hooks/use-router-for-vod-list";
import { useViewableRatingBlock } from "~/hooks/use-viewable-rating-block";
import type { G_FetchHomeQuery, G_GetGravureTopQuery } from "~/lib/graphql";

type Link = {
  id: string;
  name: string;
  path: VodHrefMap["top"] | VodHrefMap["gravure_top"] | VodHrefMap["list"] | VodHrefMap["gravure_list"];
};

export type Links = Link[];

type UseSetLinkDataArg = {
  isVideoSubPage: boolean;
  allVideoSubPages?: G_FetchHomeQuery["home"]["allVideoSubPages"];
  genreItems?: G_GetGravureTopQuery["topHeader"] | G_FetchHomeQuery["home"];
};

export const useSetLinkData = ({ allVideoSubPages, isVideoSubPage, genreItems }: UseSetLinkDataArg) => {
  const { id } = useQueryString();
  const { asPath } = useRouter();
  const { buildVodListHref } = useRouterForVodList();
  const { isAdultLinkAllowed } = useViewableRatingBlock();
  const isGravureTop = asPath.startsWith(PATHS.VOD_GRAVURE);

  const categoriesLink = useMemo(() => {
    if (!allVideoSubPages) return [];
    const links = allVideoSubPages.flatMap(({ id, title }) => {
      // グラビアの項目を除外する
      if (id === "gravure") {
        return [];
      } else {
        return { id, name: title, path: { pathname: PATHS.VOD, query: { id } } };
      }
    });
    return [
      ...links,
      // memo: (okita) "その他"をカテゴリの末尾に付与する
      OTHER_LINK,
    ];
  }, [allVideoSubPages]);

  const subPageCategories = useMemo(() => {
    if (!genreItems) return [];

    const { genres: genreList, categoryId } = genreItems;
    const fixedGenre = fixedCategoryItems.map(({ id, name, params }) => ({
      id,
      name,
      path: buildVodListHref({ query: { categories: categoryId ?? undefined, ...params } }),
    }));
    const genreLinks =
      genreList?.map(({ id, name }) => {
        return {
          id,
          name,
          path: buildVodListHref({ query: { categories: categoryId ?? undefined, genres: id } }),
        };
      }) ?? [];

    const genres = genreLinks.length !== 0 ? fixedGenre.concat(genreLinks) : [];

    return genres;
  }, [genreItems]);

  const videoHomeCategories = useMemo(() => {
    // memo: (okita) アダルト時は18禁マーク画面でカテゴリ取得不要のため早期returnさせる
    if (id === "other") return categoriesLink;

    let categoriesData = categoriesLink;

    /**
     * Video Home（id=gravureは/restrict/vod/に遷移しidがない状態なので除く）
     */
    if (id === undefined && !isGravureTop) {
      categoriesData = !isAdultLinkAllowed
        ? categoriesLink.filter((category) => category.id !== "other")
        : categoriesLink;

      return categoriesData;
    }

    return categoriesData;
  }, [id, categoriesLink, isGravureTop, isAdultLinkAllowed]);

  return { categories: !isVideoSubPage ? videoHomeCategories : subPageCategories };
};
